/**
 * AP-10 Nachtrag 2 — **E-Mail-Versand: Downloadlinks statt Anhang.**
 *
 * Die Einstellung hängt an einem eigenen Recht (`core.mail.manage`), nicht an
 * `core.tenant.update`.
 *
 * Nachtrag 3: ZIP ab einer Anzahl Dateien; Stand des Mail-Kontingents und die
 * Anfrage nach mehr Mails (`core.module.request`).
 *
 * AP-36 Phase D: feldweise ohne Knopf. Eine Stelle `mailSettings` für beide
 * Karten — sie schreiben dieselbe Mandantenzeile (`settings.mail`). Die beiden
 * PUT-Endpunkte nehmen ihr Objekt nur **vollständig**; `schreibe` ergänzt die
 * gesendeten Felder deshalb aus der Basis. „Aus" bei Microsoft 365 trennt alle
 * Postfächer und wird vorher bestätigt.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ATTACHMENT_LINK_MODES,
  ATTACHMENT_LINK_THRESHOLD_MB,
  ATTACHMENT_LINK_VALIDITY_DAYS,
  ATTACHMENT_LINK_ZIP_FROM_FILES,
  MAIL_EXTRA_QUOTA_MAX,
  MAIL_TRANSPORT_CHOICES,
  MICROSOFT365_POLICIES,
  erfuelltRecht,
  type AttachmentLinkMode,
  type MailQuotaStatus,
  type Microsoft365Policy,
} from '@symphenia/shared-types';
import { Link, useSearchParams } from 'react-router-dom';
import { fehlertext } from '@/lib/fehlertext';
import { api } from '@/lib/axios';
import { mailApi, type Microsoft365Integration, type TenantMailConnection } from '@/lib/mail/mail.api';
import { useAuthStore } from '@/lib/auth.store';
import { bestaetige } from '@/lib/dialoge';
import {
  FeldStatus,
  FeldZeile,
  feldKlasse,
  mitStand,
  useFeld,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern';

const eingabe =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50';

const zahlIn = (text: string, g: { min: number; max: number }) => {
  const n = Number(text);
  return Number.isInteger(n) && n >= g.min && n <= g.max ? n : null;
};

const imBereich = (g: { min: number; max: number }, meldung: string) => (w: number | null) =>
  w !== null && (!Number.isInteger(w) || w < g.min || w > g.max) ? meldung : null;

const nur = (o: unknown, schluessel: string[]): Datensatz => {
  const q = (o ?? {}) as Datensatz;
  return Object.fromEntries(schluessel.map((k) => [k, q[k]]));
};

/** Die Stelle der Mail-Einstellungen; `nachMicrosoft365` meldet getrennte Postfächer. */
function useMailStelle(daten: Datensatz | null, nachMicrosoft365: (entfernt: number) => void) {
  const stelleRef = useRef<Speicherstelle<Datensatz> | null>(null);
  const melde = useRef(nachMicrosoft365);
  melde.current = nachMicrosoft365;
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: 'mailSettings',
    bezeichnung: 'E-Mail-Einstellungen',
    daten,
    schreibe: async (patch, stand) => {
      const basis = stelleRef.current?.daten ?? {};
      let antwort: Datensatz | undefined;
      if (patch.attachmentLinks) {
        const attachmentLinks = nur({ ...(basis.attachmentLinks as Datensatz), ...(patch.attachmentLinks as Datensatz) }, [
          'mode',
          'thresholdMb',
          'validityDays',
          'zipFromFiles',
        ]);
        antwort = (await api.put<Datensatz>('/mail/settings', { attachmentLinks }, mitStand(stand))).data;
      }
      if (patch.microsoft365) {
        const standJetzt = ((antwort?.data as Datensatz | undefined)?.updatedAt as string | undefined) ?? stand;
        const microsoft365 = nur({ ...(basis.microsoft365 as Datensatz), ...(patch.microsoft365 as Datensatz) }, [
          'policy',
          'allowedDomains',
          'defaultTransport',
        ]);
        const r = await api.put<{ data: { removedConnections: number } }>(
          '/mail/settings/microsoft365',
          microsoft365,
          mitStand(standJetzt),
        );
        melde.current(r.data.data.removedConnections);
        antwort = (await api.get<Datensatz>('/mail/settings')).data;
      }
      return antwort ?? (await api.get<Datensatz>('/mail/settings')).data;
    },
  });
  stelleRef.current = stelle;
  return stelle;
}

export function MailSettingsSection() {
  const { t, i18n } = useTranslation();
  const auth = useAuthStore();
  const darfAnfragen = erfuelltRecht('core.module.request', auth.user?.permissions);
  const [daten, setDaten] = useState<Datensatz | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Nach dem Speichern der Microsoft-365-Richtlinie: Verbindungen neu laden. */
  const [m365Version, setM365Version] = useState(0);
  const [entfernt, setEntfernt] = useState(0);

  useEffect(() => {
    mailApi
      .getSettings()
      .then(({ data }) => setDaten(data as unknown as Datensatz))
      .catch((err) => setError(fehlertext(err, t)));
  }, [t]);

  const stelle = useMailStelle(daten, (n) => {
    setEntfernt(n);
    setM365Version((v) => v + 1);
  });
  const werte = useStellenDaten(stelle as Speicherstelle<unknown>);
  const kontingent = werte.quota as MailQuotaStatus | undefined;
  const integration = werte.microsoft365Integration as Microsoft365Integration | null | undefined;

  const zahl = (n: number) => new Intl.NumberFormat(i18n.language).format(n);

  // AP-10 Nachtrag 5: Downloadlinks betreffen nur Belege — ohne Belegwesen gibt es die Karte nicht.
  const mitBelegen = auth.hasModule('CORRESPONDENCE');

  return (
    <div className="space-y-6">
      {/* AP-10 Nachtrag 5: Versandprotokoll der Exporte */}
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-card-foreground">{t('mailLog.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('mailLog.settingsIntro')}</p>
        </div>
        <Link
          to="/settings/mail-log"
          className="inline-flex shrink-0 items-center justify-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
        >
          {t('mailLog.open')}
        </Link>
      </div>

      {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

      {mitBelegen && daten && <Downloadlinks stelle={stelle as Speicherstelle<unknown>} />}

      {kontingent && (
        <MailQuotaCard kontingent={kontingent} zahl={zahl} darfAnfragen={darfAnfragen} />
      )}

      {daten && werte.microsoft365 && integration?.available ? (
        <Microsoft365Card
          stelle={stelle as Speicherstelle<unknown>}
          integration={integration}
          version={m365Version}
          entfernt={entfernt}
        />
      ) : null}
    </div>
  );
}

function Downloadlinks({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation();
  const modus = useFeld<AttachmentLinkMode>(stelle, 'attachmentLinks.mode', {
    art: 'auswahl',
    label: t('mailSettings.mode'),
    lies: (x) => x as AttachmentLinkMode,
  });
  const schwelle = useFeld<number | null>(stelle, 'attachmentLinks.thresholdMb', {
    art: 'zahl',
    label: t('mailSettings.threshold'),
    pflicht: true,
    pruefe: imBereich(ATTACHMENT_LINK_THRESHOLD_MB, t('mailSettings.thresholdInvalid', ATTACHMENT_LINK_THRESHOLD_MB)),
  });
  const tage = useFeld<number | null>(stelle, 'attachmentLinks.validityDays', {
    art: 'zahl',
    label: t('mailSettings.validity'),
    pflicht: true,
    pruefe: imBereich(ATTACHMENT_LINK_VALIDITY_DAYS, t('mailSettings.validityInvalid', ATTACHMENT_LINK_VALIDITY_DAYS)),
  });
  const zipAb = useFeld<number | null>(stelle, 'attachmentLinks.zipFromFiles', {
    art: 'zahl',
    label: t('mailSettings.zipFromFiles'),
    pflicht: true,
    pruefe: imBereich(ATTACHMENT_LINK_ZIP_FROM_FILES, t('mailSettings.zipFromFilesInvalid', ATTACHMENT_LINK_ZIP_FROM_FILES)),
  });

  return (
    <section className="rounded-lg border border-border bg-card p-6 shadow-sm">
      <h2 className="text-base font-semibold text-card-foreground">{t('mailSettings.title')}</h2>
      <p className="mb-4 mt-1 text-sm text-muted-foreground">{t('mailSettings.intro')}</p>

      <div className="space-y-4">
        <fieldset className="space-y-2">
          <legend className="mb-1 block text-sm font-medium text-foreground">{t('mailSettings.mode')}</legend>
          {ATTACHMENT_LINK_MODES.map((m) => (
            <label key={m} className="flex items-start gap-2 text-sm text-foreground">
              <input type="radio" name="attachmentLinkMode" className="mt-1" checked={modus.wert === m} onChange={() => modus.setze(m)} />
              <span>
                {t(`mailSettings.modes.${m}`)}
                <span className="block text-xs text-muted-foreground">{t(`mailSettings.modeHints.${m}`)}</span>
              </span>
            </label>
          ))}
          <FeldStatus status={modus} />
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-3">
          <FeldZeile label={t('mailSettings.threshold')} status={schwelle} hilfe={t('mailSettings.thresholdHint')}>
            <input inputMode="numeric" className={feldKlasse} {...schwelle.props} disabled={modus.wert !== 'ABOVE_SIZE'} />
          </FeldZeile>
          <FeldZeile label={t('mailSettings.validity')} status={tage} hilfe={t('mailSettings.validityHint')}>
            <input inputMode="numeric" className={feldKlasse} {...tage.props} disabled={modus.wert === 'OFF'} />
          </FeldZeile>
          <FeldZeile label={t('mailSettings.zipFromFiles')} status={zipAb} hilfe={t('mailSettings.zipFromFilesHint')}>
            <input inputMode="numeric" className={feldKlasse} {...zipAb.props} disabled={modus.wert === 'OFF'} />
          </FeldZeile>
        </div>

        <p className="text-xs text-muted-foreground">{t('mailSettings.pdfAlwaysAttached')}</p>
      </div>
    </section>
  );
}

/** AP-10 Nachtrag 1, 13.2 — Richtlinie, erlaubte Domains, Vorwahl und die verbundenen Postfächer. */
function Microsoft365Card({
  stelle,
  integration,
  version,
  entfernt,
}: {
  stelle: Speicherstelle<unknown>;
  integration: Microsoft365Integration;
  version: number;
  entfernt: number;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [verbindungen, setVerbindungen] = useState<TenantMailConnection[]>([]);
  const [params, setParams] = useSearchParams();

  const policy = useFeld<Microsoft365Policy>(stelle, 'microsoft365.policy', {
    art: 'auswahl',
    label: t('mailSettings.microsoft365.policy'),
    lies: (x) => x as Microsoft365Policy,
  });
  const domains = useFeld<string[]>(stelle, 'microsoft365.allowedDomains', {
    label: t('mailSettings.microsoft365.allowedDomains'),
    lies: (text) => text.split(/[,;\s]+/).map((d) => d.trim()).filter(Boolean),
    schreib: (w) => (Array.isArray(w) ? w.join(', ') : ''),
  });
  const vorwahl = useFeld(stelle, 'microsoft365.defaultTransport', {
    art: 'auswahl',
    label: t('mailSettings.microsoft365.defaultTransport'),
    pflicht: true,
  });

  const ladeVerbindungen = () =>
    mailApi
      .listConnections(1, 100)
      .then(({ data }) => setVerbindungen(data.data))
      .catch((err) => setError(fehlertext(err, t)));

  useEffect(() => {
    void ladeVerbindungen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  useEffect(() => {
    if (entfernt > 0) setMeldung(t('mailSettings.microsoft365.removed', { count: entfernt }));
  }, [entfernt, version, t]);

  // Rückkehr von der Administratorzustimmung bei Microsoft.
  useEffect(() => {
    const ergebnis = params.get('mail');
    if (ergebnis !== 'consent' && ergebnis !== 'consent_error') return;
    if (ergebnis === 'consent') setMeldung(t('mailSettings.microsoft365.consentDone'));
    else setError(t('mailSettings.microsoft365.consentError'));
    const rest = new URLSearchParams(params);
    rest.delete('mail');
    setParams(rest, { replace: true });
  }, [params, setParams, t]);

  /** „Aus" trennt alle Postfächer — das wird vorher bestätigt (AP-36 6: eine Handlung im Feld). */
  const waehle = async (p: Microsoft365Policy) => {
    setMeldung(null);
    if (p === 'OFF' && policy.gespeichert !== 'OFF' && verbindungen.length > 0 && !(await bestaetige(t('mailSettings.microsoft365.offConfirm', { count: verbindungen.length })))) return;
    policy.setze(p);
  };

  const trennen = async (v: TenantMailConnection) => {
    if (!(await bestaetige(t('mailSettings.microsoft365.disconnectConfirm', { name: v.userName, address: v.address })))) return;
    setError(null);
    try {
      await mailApi.disconnectUser(v.userId);
      await ladeVerbindungen();
    } catch (err) {
      setError(fehlertext(err, t));
    }
  };

  return (
    <section className="rounded-lg border border-border bg-card p-6 shadow-sm">
      <h2 className="text-base font-semibold text-card-foreground">{t('mailSettings.microsoft365.title')}</h2>
      <p className="mb-4 mt-1 text-sm text-muted-foreground">{t('mailSettings.microsoft365.intro')}</p>

      <div className="space-y-4">
        <fieldset className="space-y-2">
          <legend className="mb-1 block text-sm font-medium text-foreground">{t('mailSettings.microsoft365.policy')}</legend>
          {MICROSOFT365_POLICIES.map((p) => (
            <label key={p} className="flex items-start gap-2 text-sm text-foreground">
              <input type="radio" name="microsoft365Policy" className="mt-1" checked={policy.wert === p} onChange={() => void waehle(p)} />
              <span>
                {t(`mailSettings.microsoft365.policies.${p}`)}
                <span className="block text-xs text-muted-foreground">{t(`mailSettings.microsoft365.policyHints.${p}`)}</span>
              </span>
            </label>
          ))}
          <FeldStatus status={policy} />
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <FeldZeile label={t('mailSettings.microsoft365.allowedDomains')} status={domains} hilfe={t('mailSettings.microsoft365.allowedDomainsHint')}>
            <input placeholder="firma.at" className={feldKlasse} {...domains.props} disabled={policy.wert === 'OFF'} />
          </FeldZeile>
          <FeldZeile label={t('mailSettings.microsoft365.defaultTransport')} status={vorwahl} hilfe={t('mailSettings.microsoft365.defaultTransportHint')}>
            <select className={feldKlasse} {...vorwahl.props} disabled={policy.wert !== 'OPTIONAL'}>
              {MAIL_TRANSPORT_CHOICES.map((c) => (
                <option key={c} value={c}>
                  {t(`mailSettings.microsoft365.transports.${c}`)}
                </option>
              ))}
            </select>
          </FeldZeile>
        </div>

        {integration.adminConsentUrl && policy.wert !== 'OFF' && (
          <div className="rounded-md border border-border p-3 text-sm">
            <p className="text-foreground">{t('mailSettings.microsoft365.adminConsent')}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t('mailSettings.microsoft365.adminConsentHint')}</p>
            <a href={integration.adminConsentUrl} className="mt-2 inline-block text-sm text-primary hover:underline">
              {t('mailSettings.microsoft365.adminConsentOpen')}
            </a>
          </div>
        )}

        <div>
          <p className="mb-1 text-sm font-medium text-foreground">{t('mailSettings.microsoft365.connections')}</p>
          {verbindungen.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('mailSettings.microsoft365.noConnections')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  {verbindungen.map((v) => (
                    <tr key={v.userId} className="border-t border-border">
                      <td className="py-1.5 pr-3 text-foreground">{v.userName}</td>
                      <td className="py-1.5 pr-3 text-muted-foreground">{v.address}</td>
                      <td className="py-1.5 pr-3 text-xs">{t(`mailConnection.status.${v.status}`)}</td>
                      <td className="py-1.5 text-right">
                        <button type="button" onClick={() => void trennen(v)} className="text-xs text-destructive hover:underline">
                          {t('mailSettings.microsoft365.disconnect')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {error && <p className="mt-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
      {meldung && <p className="mt-4 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{meldung}</p>}
    </section>
  );
}

/** Nachtrag 3, 6.4 — Verbrauch des Monats und die Anfrage nach mehr Mails. */
function MailQuotaCard({
  kontingent,
  zahl,
  darfAnfragen,
}: {
  kontingent: MailQuotaStatus;
  zahl: (n: number) => string;
  darfAnfragen: boolean;
}) {
  const { t } = useTranslation();
  const [offen, setOffen] = useState(false);
  const [menge, setMenge] = useState('1000');
  const [notiz, setNotiz] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const anteil = kontingent.limit > 0 ? Math.min(100, Math.round((kontingent.used / kontingent.limit) * 100)) : 100;
  const [jahr, monat] = kontingent.month.split('-').map(Number);
  const monatsname = new Date(Date.UTC(jahr, monat - 1, 15)).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const anfragen = async () => {
    setFehler(null);
    const n = zahlIn(menge, { min: 1, max: MAIL_EXTRA_QUOTA_MAX });
    if (n === null) {
      setFehler(t('mailSettings.quota.requestInvalid', { max: zahl(MAIL_EXTRA_QUOTA_MAX) }));
      return;
    }
    setLaeuft(true);
    try {
      const ergebnis = await mailApi.requestMoreMails(n, notiz);
      setMeldung(
        t(ergebnis.status === 'SENT' ? 'mailSettings.quota.requestSent' : 'mailSettings.quota.requestAlreadySent', {
          date: new Date(ergebnis.requestedAt).toLocaleString(),
        }),
      );
      setOffen(false);
    } catch (err) {
      setFehler(fehlertext(err, t));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
      <h2 className="text-base font-semibold text-card-foreground">{t('mailSettings.quota.title')}</h2>
      <p className="mb-4 mt-1 text-sm text-muted-foreground">{t('mailSettings.quota.intro')}</p>

      <p className="text-sm text-foreground">
        {t('mailSettings.quota.usage', { used: zahl(kontingent.used), limit: zahl(kontingent.limit), month: monatsname })}
      </p>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full ${anteil >= 100 ? 'bg-destructive' : anteil >= 80 ? 'bg-amber-500' : 'bg-primary'}`}
          style={{ width: `${anteil}%` }}
        />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {t('mailSettings.quota.composition', { base: zahl(kontingent.base), extra: zahl(kontingent.extra) })}
      </p>

      {meldung && <p className="mt-4 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{meldung}</p>}

      {darfAnfragen && !offen && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => {
              setMeldung(null);
              setOffen(true);
            }}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-muted"
          >
            {t('mailSettings.quota.request')}
          </button>
        </div>
      )}

      {offen && (
        <div className="mt-4 space-y-3 rounded-md border border-border p-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">{t('mailSettings.quota.requestAmount')}</label>
            <input type="number" min={1} max={MAIL_EXTRA_QUOTA_MAX} step={1} value={menge} onChange={(e) => setMenge(e.target.value)} className={eingabe} />
            <p className="mt-1 text-xs text-muted-foreground">{t('mailSettings.quota.requestHint')}</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">{t('mailSettings.quota.requestNote')}</label>
            <textarea rows={2} maxLength={2000} value={notiz} onChange={(e) => setNotiz(e.target.value)} className={`${eingabe} resize-y`} />
          </div>
          {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOffen(false)} className="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-muted">
              {t('common.cancel')}
            </button>
            <button
              type="button"
              disabled={laeuft}
              onClick={() => void anfragen()}
              className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {laeuft ? t('common.loading') : t('mailSettings.quota.requestSubmit')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
